import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { BackToDashboard } from "../components/BackToDashboard";
import { ArrowUpDown, AlertTriangle } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const STATUSES = ["All", "Pending Review", "Approved", "Inspection Scheduled", "Inspection Passed", "Denied"];
const TYPES = ["All", "Building", "Electrical", "Plumbing", "Mechanical", "Sprinkler", "Demolition"];
const BOROUGHS = ["All", "Manhattan", "Brooklyn", "Queens", "Bronx", "Staten Island"];

export default function Permits() {
  const [permits, setPermits] = useState([]);
  const [status, setStatus] = useState("All");
  const [type, setType] = useState("All");
  const [borough, setBorough] = useState("All");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [sort, setSort] = useState({ key: "days_in_status", dir: -1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/permits")
      .then(r => setPermits(Array.isArray(r.data) ? r.data : []))
      .catch(e => console.error("Permits load failed:", e))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    let r = [...permits];
    if (status !== "All") r = r.filter(p => p.status === status);
    if (type !== "All") r = r.filter(p => p.permit_type === type);
    if (borough !== "All") r = r.filter(p => p.borough === borough);
    if (flaggedOnly) r = r.filter(p => p.flagged);
    r.sort((a, b) => ((a[sort.key] || 0) > (b[sort.key] || 0) ? 1 : -1) * sort.dir);
    return r;
  }, [permits, status, type, borough, flaggedOnly, sort]);

  const toggleSort = (k) => setSort(s => ({ key: k, dir: s.key === k ? -s.dir : -1 }));

  return (
    <div className="px-4 sm:px-8 py-8 max-w-7xl mx-auto" data-testid="permits-page">
      <BackToDashboard />
      <span className="label-eyebrow">Global View</span>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-6">All Permits</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUSES} testId="filter-status" />
        <FilterSelect label="Type" value={type} onChange={setType} options={TYPES} testId="filter-type" />
        <FilterSelect label="Borough" value={borough} onChange={setBorough} options={BOROUGHS} testId="filter-borough" />
        <label className="inline-flex items-center gap-2 text-sm px-3 py-1.5 border border-zinc-200 rounded-sm bg-white cursor-pointer" data-testid="filter-flagged">
          <input type="checkbox" checked={flaggedOnly} onChange={e => setFlaggedOnly(e.target.checked)} className="accent-zinc-950" />
          Flagged only
        </label>
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <Th>Job</Th><Th>Address</Th>
              <SortableTh label="Permit #" k="permit_number" sort={sort} onClick={toggleSort} />
              <Th>Type</Th>
              <Th>Status</Th>
              <SortableTh label="Days in status" k="days_in_status" sort={sort} onClick={toggleSort} />
              <Th>Flag</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <Td><Skeleton className="h-4 w-24" /></Td>
                    <Td><Skeleton className="h-4 w-32" /></Td>
                    <Td><Skeleton className="h-4 w-20 font-mono" /></Td>
                    <Td><Skeleton className="h-4 w-16" /></Td>
                    <Td><Skeleton className="h-5 w-24 rounded-full" /></Td>
                    <Td><Skeleton className="h-4 w-8" /></Td>
                    <Td><Skeleton className="h-4 w-8" /></Td>
                  </tr>
                ))}
              </>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500" data-testid="permits-empty">No permits match your filters.</td></tr>
            ) : rows.map(p => (
              <tr key={p.permit_id} className="row-hover border-b border-zinc-100" data-testid={`permit-row-${p.permit_id}`}>
                <Td>{p.job_nickname}</Td>
                <Td className="text-zinc-600">{p.address}</Td>
                <Td className="font-mono">{p.permit_number}</Td>
                <Td>{p.permit_type}</Td>
                <Td><StatusBadge status={p.status} /></Td>
                <Td>{p.days_in_status}</Td>
                <Td>{p.flagged ? <span className="inline-flex items-center gap-1 text-xs text-yellow-700"><AlertTriangle size={12} /> Delayed</span> : "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500 mt-3">{rows.length} of {permits.length} permits</p>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-2.5 text-left label-eyebrow font-semibold">{children}</th>;
const SortableTh = ({ label, k, sort, onClick }) => (
  <th className="px-4 py-2.5 text-left">
    <button onClick={() => onClick(k)} className="label-eyebrow font-semibold inline-flex items-center gap-1 hover:text-zinc-950" data-testid={`sort-${k}`}>
      {label} <ArrowUpDown size={11} className={sort.key === k ? "text-zinc-950" : "text-zinc-400"} />
    </button>
  </th>
);
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div className="flex items-center gap-1.5">
    <span className="label-eyebrow">{label}</span>
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
      className="text-sm px-2 py-1.5 border border-zinc-200 rounded-sm bg-white">
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  </div>
);
